import { Component, computed, inject, input, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { parseIso8601, RequestError, Store, toIsoDate } from '@softline/core';
import {
  MessageBarStore,
  Modal,
  ModalStore,
  ResolvableModal,
  SOFTLINE_FEATURE_MESSAGE_BAR,
  SOFTLINE_FEATURE_MODAL } from '@softline/ui-core';
import { TimeReport } from '../../data/time-report.model';
import {
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup } from '@angular/forms';
import { Ticket } from '../../data/ticket.model';
import {
  authorizationFn,
  UserInfoService } from '@softline/auth';
import { map } from 'rxjs/operators';
import {
  SOFTLINE_FEATURE_TICKET_TIME_REPORT,
  SOFTLINE_PERMISSION_TICKET_EDITOR,
  TicketTimeReportStore } from '../../ticket.shared';
import { showRequestErrors } from '@softline/application';
import { CommonModule } from '@angular/common';

import { DateInputComponent } from '@softline/ui-core/date-input';
import { DurationInputComponent } from '@softline/ui-core/date-input';
import { FormatPipe } from '@softline/ui-core';
import { IconComponent } from '@softline/ui-core';
import { ToolTipDirective } from '@softline/ui-core';
import { TranslatePipe } from '@softline/ui-core';
@Component({
  selector: 'soft-time-report-dialog',
  imports: [CommonModule, ReactiveFormsModule,
    DateInputComponent,
    DurationInputComponent,
    FormatPipe,
    IconComponent,
    ToolTipDirective,
    TranslatePipe],
  templateUrl: './time-report-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./time-report-dialog.component.css'] })
export class TimeReportDialogComponent extends ResolvableModal<any> implements OnInit {
  private store = inject(Store);
  private readonly userInfo = inject(UserInfoService);
  private readonly isAuthorized = authorizationFn();

  // close!: (result: undefined) => void;
  action: undefined | 'create' | 'update' | 'delete';

  //ticket!: Ticket;
  ticket = input.required<Ticket>();
  timeReports$ = this.store.observe(
    SOFTLINE_FEATURE_TICKET_TIME_REPORT,
    TicketTimeReportStore.getters.all
  );

  openedModals$ = this.store
    .observe(SOFTLINE_FEATURE_MODAL, ModalStore.getters.items)
    .pipe(map((o) => o?.length));

  form = new UntypedFormGroup({
    id: new UntypedFormControl(undefined),
    date: new UntypedFormControl(undefined),
    duration: new UntypedFormControl(undefined),
    person: new UntypedFormControl(undefined),
    ticketId: new UntypedFormControl(undefined) });

  readonly seeAllPermission = this.isAuthorized(SOFTLINE_PERMISSION_TICKET_EDITOR);

  readonly personalId = computed(() => this.userInfo.value()?.employeeId);

  ngOnInit(): void {}

  // registerCloseHandler(handler: (result: undefined) => void): void {
  //   this.close = handler;
  // }

  isToday(date: string | undefined): boolean {
    if (!date) return true; // moment(undefined) was "now"
    const time = parseIso8601(date);
    return time !== null && toIsoDate(new Date(time)) === toIsoDate(new Date());
  }

  async edit(
    timeReport: TimeReport | undefined,
    action: 'create' | 'update' | 'delete'
  ): Promise<void> {
    const canSeeAllTimeReports = this.isAuthorized(SOFTLINE_PERMISSION_TICKET_EDITOR)();
    const personId = this.userInfo.value()?.employeeId;
    if (canSeeAllTimeReports && timeReport && timeReport?.person?.id !== personId) {
      return;
    }

    this.action = action;
    if (timeReport) {
      this.form.patchValue(timeReport);
    } else {
      this.form.reset();
    }
    if (action === 'create') {
      this.form.patchValue({
        ticketId: this.ticket().id,
        date: toIsoDate(new Date()), //set today as default
      });
    }
  }

  cancelEdit(): void {
    this.action = undefined;
    this.form.reset();
  }

  async save(
    timeReport: TimeReport,
    action: 'create' | 'update' | 'delete'
  ): Promise<void> {
    switch (action) {
      case 'create':
        await this.createTimeReport(
          timeReport,
          this.getMessageBarFactoryParams('CREATE')
        );
        break;
      case 'update':
        await this.updateTimeReport(
          timeReport,
          this.getMessageBarFactoryParams('UPDATE')
        );
        break;
      case 'delete':
        await this.deleteTimeReport(
          timeReport,
          this.getMessageBarFactoryParams('DELETE')
        );
        break;
      default:
        break;
    }
    this.resolve({ timeReport, action });
  }

  private async createTimeReport(timeReport: TimeReport, params: any) {
    try {
      await this.store.dispatch(
        SOFTLINE_FEATURE_TICKET_TIME_REPORT,
        TicketTimeReportStore.actions.create,
        { entity: timeReport }
      );
      await this.store.dispatch(
        SOFTLINE_FEATURE_MESSAGE_BAR,
        MessageBarStore.actions.success,
        (params.message += 'SUCCESS')
      );
    } catch (e) {
      if (e instanceof RequestError) {
        await this.store.dispatch(
          SOFTLINE_FEATURE_MESSAGE_BAR,
          MessageBarStore.actions.error,
          (params.message += 'FAILURE')
        );
      } else {
        showRequestErrors(this.store, e);
      }
    }
  }

  private async updateTimeReport(timeReport: TimeReport, params: any) {
    try {
      await this.store.dispatch(
        SOFTLINE_FEATURE_TICKET_TIME_REPORT,
        TicketTimeReportStore.actions.update,
        { entity: timeReport }
      );
      await this.store.dispatch(
        SOFTLINE_FEATURE_MESSAGE_BAR,
        MessageBarStore.actions.success,
        (params.message += 'SUCCESS')
      );
    } catch (e) {
      if (e instanceof RequestError) {
        await this.store.dispatch(
          SOFTLINE_FEATURE_MESSAGE_BAR,
          MessageBarStore.actions.error,
          (params.message += 'FAILURE')
        );
      } else {
        showRequestErrors(this.store, e);
      }
    }
  }

  private async deleteTimeReport(timeReport: TimeReport, params: any) {
    try {
      await this.store.dispatch(
        SOFTLINE_FEATURE_TICKET_TIME_REPORT,
        TicketTimeReportStore.actions.delete,
        { entity: timeReport }
      );
      await this.store.dispatch(
        SOFTLINE_FEATURE_MESSAGE_BAR,
        MessageBarStore.actions.success,
        (params.message += 'SUCCESS')
      );
      this.action = undefined;
      this.form.reset();
    } catch (e) {
      if (e instanceof RequestError) {
        await this.store.dispatch(
          SOFTLINE_FEATURE_MESSAGE_BAR,
          MessageBarStore.actions.error,
          (params.message += 'FAILURE')
        );
      } else {
        showRequestErrors(this.store, e);
      }
    }
  }

  private getMessageBarFactoryParams(actionType: string): any {
    return {
      message: '#TICKETS.DIALOGS.TIME_REPORT.' + actionType + '.' };
  }
}
